<template>
  <SidebarInnerWrapper :placement="SidebarPlacement.Left" class="page-navigation-bar">
    <slot name="top" />
    <div class="menu">
      <FriedList v-if="pages.length" class="page-list">
        <li
          v-for="page of visiblePages"
          :key="page.id"
          class="page-list-item"
          @click="$emit('select', page.id)"
        >
          <BlockListItem
            :block="page.editorBlock"
            :is-current-block="currentPageId === page.id"
            :participant="participant"
            :locked-for-cc="lockedPageIds.includes(page.id)"
            @navigate-to-section="
              $emit('navigate-to-section', { selection: $event, pageId: page.id })
            "
          />
        </li>
      </FriedList>
    </div>
    <button
      class="powered-by"
      data-external="dsr-powered-by-button"
      :aria-label="t('powered-by-getaccept')"
      @click="handlePoweredByClick"
    >
      <PoweredByLogo />
    </button>
  </SidebarInnerWrapper>
</template>
<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import type { Page } from '@getaccept/dsr-shared-new/src/types/page';
import { SidebarPlacement } from '@getaccept/dsr-shared-new/src/types/sidebar-placement';
import SidebarInnerWrapper from '@getaccept/dsr-shared-new/src/components/SidebarInnerWrapper.vue';
import type { Participant } from '@getaccept/dsr-shared-new';
import { useI18n } from 'vue-i18n';
import PoweredByLogo from './../../assets/powered-by-ga.svg';
import BlockListItem from './BlockListItem.vue';

export default defineComponent({
  name: 'PageNavigationBar',
  components: {
    SidebarInnerWrapper,
    BlockListItem,
    PoweredByLogo,
  },
  props: {
    pages: {
      default: () => [],
      type: Array as PropType<Page[]>,
    },
    currentPageId: {
      type: String,
      default: null,
    },
    participant: {
      type: Object as PropType<Participant>,
      default: null,
    },
    lockedPageIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  setup(props) {
    const { t } = useI18n();
    const handlePoweredByClick = () => window.open('https://www.getaccept.com', '_blank');

    const visiblePages = computed(() =>
      props.pages.filter(
        ({ editorBlock }) => !editorBlock.content.sections.every(({ hidden }) => hidden)
      )
    );

    return {
      SidebarPlacement,
      handlePoweredByClick,
      PoweredByLogo,
      visiblePages,
      t,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@getaccept/dsr-shared-new/src/scss/page-navigation-bar';

$powered-by-height: 22px;

.page-navigation-bar {
  height: 100%;

  .menu {
    flex: auto;
  }
}

.powered-by {
  animation: bounce 2s;
  height: calc(#{$powered-by-height} + #{var(--spacing-50)} * 2);
  cursor: pointer;
  border: none;
  background: none;
  padding: var(--spacing-50) 0;
  margin: 0 auto;

  &:focus {
    outline-color: var(--gray-98);
  }
}

@keyframes bounce {
  0% {
    transform: scale(0);
  }

  80% {
    transform: scale(0);
  }

  90% {
    transform: scale(1.5);
  }

  100% {
    transform: scale(1);
  }
}
</style>
